import { useEffect, useRef, useState } from 'react';

export interface ScriptProps {
  src: string;
  onLoad(): void;
  async?: boolean;
  [dataAttribute: `data-${string}`]: string;
}

export function Script({ src, onLoad, async, ...props }: ScriptProps) {
  const scriptRef = useRef<HTMLScriptElement>();
  const [isLoaded, setIsLoaded] = useState(false);

  const onLoadRef = useRef(onLoad);
  onLoadRef.current = onLoad;

  useEffect(() => {
    if (!isLoaded) return;

    const script = scriptRef.current;
    if (!script) return;

    Object.entries(props).forEach(([key, value]) => {
      if (key.startsWith('data-')) {
        script.setAttribute(key, value as string);
      }
    });

    if (typeof async !== 'undefined') {
      script.async = async;
    }
  }, [isLoaded, async, props]);

  useEffect(() => {
    if (scriptRef.current) return;

    const script = document.createElement('script');

    Object.entries(props).forEach(([key, value]) => {
      if (key.startsWith('data-')) {
        script.setAttribute(key, value as string);
      }
    });

    if (typeof async !== 'undefined') {
      script.async = async;
    }

    function handleLoad() {
      onLoadRef.current();
      setIsLoaded(true);
    }
    script.addEventListener('load', handleLoad);

    script.src = src;
    document.head.appendChild(script);
    scriptRef.current = script;

    return () => {
      scriptRef.current = undefined;
      setIsLoaded(false);
      document.head.removeChild(script);
      script.removeEventListener('load', handleLoad);
    };
  }, [src]);

  return null;
}
